"use client";

import * as React from "react";
import { usePathname } from "next/navigation";
import { Layers, ListOrdered, PanelLeft } from "lucide-react";
import AdSenseSidebar from "@/components/AdSenseSidebar";
import { parseForumProblemPath } from "@/lib/route-layout";
import { ADSENSE_SLOTS } from "@/lib/adsense";
import { cn } from "@/lib/utils";
import Section from "@/components/article-shell/Section";
import SolutionAside from "@/components/article-shell/SolutionAside";
import ShellFrame, { RAIL_WIDTH } from "@/components/article-shell/ShellFrame";
import {
  clampRatio,
  usePersistedWidth,
  useCompactAutoCollapse,
} from "@/components/article-shell/shell-primitives";
import {
  SlotRegistryContext,
  SolutionModeContext,
  emptySlotRegistry,
  solutionOnlyMode,
} from "@/components/article-shell/ArticleShellContext";
import Search from "@/components/Search";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { useIsCompactShell } from "@/hooks/useViewport";
import CategoryMap from "./CategoryMap";
import SiblingNav from "./SiblingNav";

/**
 * 문제 포럼 상세(`/forum/jcg/<subject>/<scope>/<problemId>`) 경량 셸 (JCG-2105 + 풀이 모드 /
 * JCG-2142 ShellFrame 이주).
 *
 * noindex 내부 도구라 ArticleShell 의 점수·지도·우측 학습 패널은 빼되, 광고와 풀이 모드(우측 변신)는
 * ArticleShell 정책에 맞춘다("풀이/오답노트는 셸이 보여주는 그대로 따라간다"):
 *  - 좌측 사이드바: 아이콘 레일(1열) + "전체 범위" 지도 + "관련 문제" Section(2열, 같은 scope 네비).
 *  - 중앙 main: 슬림 헤더(사이드바 토글 + 검색) + children(문제+토론, 이미 mx-auto max-w-4xl) + footer.
 *  - 우측 컬럼: free·비로그인 사용자에게는 사이드바 광고. 문제를 풀어 ProblemAnswer 가
 *    enterSolution 을 호출하면 우측이 풀이+오답노트로 변신(3컬럼). chrome 은 SolutionAside 공유
 *    컴포넌트라 ArticleShell 과 동일.
 *
 * 셸 chrome(IconRail·sticky 좌/우 aside·그리드·드래그 리사이즈)은 ShellFrame 이 단일 소유한다.
 * 이 wrapper 는 좌측 콘텐츠(지도·관련문제)·좌측 접힘·풀이 모드 상태/슬롯·폭 영속(ui2-forum-*)을
 * 소유하고, ShellFrame 의 columns 를 광고/풀이 모드일 때 left+main+right, 광고 미노출 평상시
 * left+main 으로 가른다.
 *
 * 슬림 헤더는 ContentHeader 재사용 금지(`#article-h1` 핀 로직이 포럼에 안 맞음) — 토글 + 검색만
 * 직접 렌더해 ShellFrame 의 `renderHeader` 로 넘긴다.
 *
 * ProblemAnswer 는 inShell+isAdFree 일 때 ArticleShellContext 의 enterSolution/슬롯을 소비해
 * 풀이 본문을 solutionSlot, 오답노트를 errorNoteSlot 에 createPortal 한다(ProblemAnswer 무수정).
 * 따라서 이 셸이 children 을 ArticleShellContext.Provider 로 감싸고 풀이 모드 상태/슬롯을 제공하면
 * 포럼에서도 셸과 동일하게 동작한다.
 *
 * 데스크탑 전용 — LayoutSwitch 가 ≥1024px 일 때만 렌더하므로 내부에서 미디어 판정하지 않는다.
 *
 * 스크롤은 ArticleShell 과 동일하게 문서(window) 기준: 사이드바는 sticky top-0 h-dvh self-start,
 * 가운데는 일반 문서 흐름, 슬림 헤더는 sticky top-0.
 *
 * 색상은 전역 디자인 토큰 + 브랜드 앰버만 사용해 다크모드 자동 대응(하드코딩 bg-white/gray 금지).
 * 풀이 모드 우측 aside 배경은 ArticleShell 원본 그대로 `bg-gray-50 dark:bg-gray-900/40`.
 */
const LEFT_MIN = 240;
const LEFT_MAX = 420;
const LEFT_DEFAULT = 320;
// RAIL_WIDTH(접힘 시 좌측 컬럼 폭)는 ShellFrame 단일 export 공유(JCG-2142).
// 풀이 모드 우측 폭 상수 — ArticleShell 과 동일 시맨틱(JCG-2065).
const SOLUTION_DEFAULT = 560; // 풀이 이미지 가독성을 위한 기본 폭
const SOLUTION_RAIL = 52; // 풀이 "숨기기" 시 우측 레일 폭
const RIGHT_MIN = 300; // 풀이 모드 우측 폭 clamp
const RIGHT_MAX = 680;
const AD_RIGHT_WIDTH = 300; // AdSenseSidebar 자체 고유 폭과 동일
const SPLIT_RATIO_MIN = 0.22; // 풀이/오답 50/50 분할 클램프
const SPLIT_RATIO_MAX = 0.78;
const LS_LEFT_W = "ui2-forum-left-w";
const LS_SOLUTION_W = "ui2-forum-solution-w"; // 풀이 모드 우측 폭(px)

interface ForumProblemShellProps {
  children: React.ReactNode;
  footer: React.ReactNode;
  globals: React.ReactNode;
}

export default function ForumProblemShell({
  children,
  footer,
  globals,
}: ForumProblemShellProps) {
  const pathname = usePathname() ?? "";
  const parsed = parseForumProblemPath(pathname);
  const problemId = parsed?.problemId;

  // 컴팩트 셸 구간(1024~1279px, JCG-2224): 셸은 유지하되 좌측을 레일로 접고 풀이 폭을 캡해
  // 본문 폭을 확보한다. 1280px↑ 는 false 라 기존 경험 불변.
  const compact = useIsCompactShell();
  const { isAdFree, isLoaded: passLoaded } = useEffectivePass();
  const sidebarAdSlot = ADSENSE_SLOTS.sidebarResponsive ?? ADSENSE_SLOTS.sidebar;

  // 서버 기본폭 렌더 → 마운트 후 LS 복원(lazy init 금지). setter 는 clamp+영속 일체(JCG-2138).
  const [leftW, onResizeLeft] = usePersistedWidth(LS_LEFT_W, LEFT_DEFAULT, LEFT_MIN, LEFT_MAX);
  // leftCollapsed 는 세션 전용(ArticleShell 과 동일) — 사용자 토글과 컴팩트 구간 변화가 소유한다.
  // localStorage 에 영속하지 않아 drift 가 없다.
  const [leftCollapsed, setLeftCollapsed] = React.useState(false);
  // 좌측 두 Section 펼침 상태(세션 전용): mapOpen = "전체 범위"(CategoryMap),
  // navOpen = "관련 문제"(SiblingNav).
  const [mapOpen, setMapOpen] = React.useState(true);
  const [navOpen, setNavOpen] = React.useState(true);

  // 풀이 모드(JCG-2065 와 동일): 우측 사이드바 광고(panels) ↔ "풀이"(solution)로 변신.
  // ProblemAnswer 가 enterSolution(id) 을 호출하면 우측이 열린다. rightMode/wrongOpen/splitRatio/
  // activeSolutionId 는 세션 상태(localStorage 불필요), solutionW만 영속(ui2-forum-solution-w).
  const [rightMode, setRightMode] = React.useState<"panels" | "solution">("panels");
  // 풀이 모드 우측 폭 — 단일 폭·고정 키/min·max 라 usePersistedWidth 로 통일(영속 setter=onResizeRight).
  const [solutionW, onResizeRight] = usePersistedWidth(
    LS_SOLUTION_W,
    SOLUTION_DEFAULT,
    RIGHT_MIN,
    RIGHT_MAX,
  );
  const [solutionCollapsed, setSolutionCollapsed] = React.useState(false);
  const [wrongOpen, setWrongOpen] = React.useState(false);
  const [splitRatio, setSplitRatio] = React.useState(0.5);
  const [activeSolutionId, setActiveSolutionId] = React.useState<string | null>(null);
  const [solutionSlot, setSolutionSlot] = React.useState<HTMLElement | null>(null);
  const [errorNoteSlot, setErrorNoteSlot] = React.useState<HTMLElement | null>(null);
  const solutionSplitRef = React.useRef<HTMLDivElement>(null);

  // 폭 복원/영속은 usePersistedWidth(좌측·풀이폭)가 일괄 처리(마운트 후 LS 복원·clamp·기록).
  // 서버 기본값 렌더 → 마운트 후 복원 패턴은 ArticleShell 과 동일(lazy init 금지, JCG-2138).

  const toggleSidebar = React.useCallback(() => {
    setLeftCollapsed((prev) => !prev);
  }, []);

  const toggleMap = React.useCallback(() => setMapOpen((v) => !v), []);
  const toggleNav = React.useCallback(() => setNavOpen((v) => !v), []);

  // 풀이 모드 진입/종료(ArticleShell 과 동일 시맨틱). 풀이 열기/닫기는 우측 패널 상태만 바꾸고,
  // 좌측 메뉴 접힘 상태는 사용자 토글 또는 컴팩트 구간 변화가 소유한다.
  const enterSolution = React.useCallback((id: string) => {
    setActiveSolutionId(id);
    setRightMode("solution");
    setWrongOpen(false);
    setSolutionCollapsed(false);
  }, []);

  const exitSolution = React.useCallback(() => {
    setRightMode("panels");
    setWrongOpen(false);
    setActiveSolutionId(null);
    setSolutionCollapsed(false);
  }, []);

  const expandSolution = React.useCallback(() => setSolutionCollapsed(false), []);

  // 컴팩트 셸(1024~1279, JCG-2224): 진입 시 좌측을 레일로 접어 본문 폭을 확보한다.
  // 사용자가 헤더 토글로 다시 열 수 있고(같은 구간 내에선 재적용 안 함), 구간 경계를 넘는
  // 리사이즈에서만 다시 적용한다. 풀이 모드 열기/닫기는 이 상태를 덮어쓰지 않는다.
  useCompactAutoCollapse(compact, setLeftCollapsed);

  // 관련 문제로 소프트 네비게이션 시 풀이 모드 리셋(JCG-2105). 셸은 레이아웃 레벨이라
  // 언마운트되지 않으므로, 문제 A에서 풀이를 연 채 SiblingNav로 문제 B로 이동하면 A의
  // ProblemAnswer 는 언마운트되지만 셸의 rightMode/activeSolutionId 는 'A' 그대로 남아
  // 우측에 빈 풀이 패널이 보인다. problemId 변화 시 exitSolution 으로 panels 로 되돌린다.
  // 최초 마운트(prev === 현재)에는 동작하지 않아 localStorage 복원·초기 상태를 건드리지 않는다.
  const prevProblemIdRef = React.useRef(problemId);
  React.useEffect(() => {
    if (prevProblemIdRef.current === problemId) return;
    prevProblemIdRef.current = problemId;
    exitSolution();
  }, [problemId, exitSolution]);

  // 풀이 ↕ 오답노트 50/50 분할 핸들 — clientY 를 분할 컨테이너 상대 비율로 변환(클램프 0.22~0.78).
  const onResizeSolutionSplit = React.useCallback((clientY: number) => {
    const el = solutionSplitRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const ratio = (clientY - r.top) / r.height;
    // 분수 비율은 반올림 없는 clampRatio 사용(공유 clamp 은 Math.round 포함 px 전용). ArticleShell 과 동일 (JCG-2156).
    setSplitRatio(clampRatio(ratio, SPLIT_RATIO_MIN, SPLIT_RATIO_MAX));
  }, []);

  const isSolution = rightMode === "solution";
  const showAdAside =
    rightMode === "panels" && passLoaded && !isAdFree && !!sidebarAdSlot;
  const hasRightColumn = isSolution || showAdAside;

  // 접힘 시 1열(아이콘 레일, 58px)만 남기고 2열(전체 범위·관련 문제)을 숨긴다.
  // 우측은 광고 또는 풀이 모드일 때만 열린다. 풀이 모드 열기/닫기는 좌측 상태를 바꾸지 않는다.
  const leftWidthCss = leftCollapsed ? `${RAIL_WIDTH}px` : `${leftW}px`;
  // 풀이 이미지는 컴팩트 셸에서도 저장된 폭(최대 680px)을 그대로 사용한다.
  const rightWidthCss = isSolution
    ? solutionCollapsed
      ? `${SOLUTION_RAIL}px`
      : `${solutionW}px`
    : `${AD_RIGHT_WIDTH}px`;

  // children 에 내려줄 셸 컨텍스트 (JCG-2140 도메인 분할).
  // 이 경량 셸은 좌/우/헤더 정적 슬롯·점수·필기·출석·작성(compose) 등을 일절 쓰지 않으므로,
  // 슬롯 레지스트리는 emptySlotRegistry(true)로 한 번에 비워(과거 17개 null/noop 박제 제거),
  // 풀이 모드만 solutionOnlyMode 로 채운다 — ProblemAnswer 가 소비하는 필드만 실제 값.
  const slotRegistry = React.useMemo(() => emptySlotRegistry(true), []);
  const solutionMode = React.useMemo(
    () =>
      solutionOnlyMode({
        rightMode,
        solutionSlot,
        errorNoteSlot,
        activeSolutionId,
        wrongOpen,
        solutionCollapsed,
        enterSolution,
        exitSolution,
        setWrongOpen,
      }),
    [
      rightMode,
      solutionSlot,
      errorNoteSlot,
      activeSolutionId,
      wrongOpen,
      solutionCollapsed,
      enterSolution,
      exitSolution,
    ],
  );

  // 좌측 사이드바 콘텐츠 (레일·접힘·리사이즈 핸들은 ShellFrame 소유). 두 Section 세로 스택:
  // (위) "전체 범위"(CategoryMap) + (아래) "관련 문제"(SiblingNav). 둘 다 자연 높이로 쌓이고,
  // 콘텐츠가 길어지면 사이드바 내부에서만 스크롤된다(overflow-y-auto, 가운데 본문 문서 스크롤은 무관).
  const leftContent = (
    <div className="flex min-w-0 flex-1 flex-col gap-2 overflow-y-auto p-3">
      {/* (위) 전체 범위 지도 — 다른 과목·범위 탐색. parsed 가 있을 때만 렌더. */}
      {parsed && (
        <Section
          icon={Layers}
          label="전체 범위"
          amber
          open={mapOpen}
          onToggle={toggleMap}
        >
          <CategoryMap
            currentScope={parsed.scope}
            currentProblemId={parsed.problemId}
          />
        </Section>
      )}

      {/* (아래) 관련 문제 — 현재 scope 의 문제 목록(라벨 "형제 문제" → "관련 문제"). */}
      <Section
        icon={ListOrdered}
        label="관련 문제"
        amber
        open={navOpen}
        onToggle={toggleNav}
      >
        {parsed && (
          <SiblingNav
            subject={parsed.subject}
            scope={parsed.scope}
            currentId={parsed.problemId}
          />
        )}
      </Section>
    </div>
  );

  // 슬림 헤더 — ContentHeader(핀 제목/breadcrumb) 대신 토글 + 검색만 단순하게.
  // 포럼 상세엔 #article-h1 핀 로직이 맞지 않아 ContentHeader 를 재사용하지 않는다(ShellFrame renderHeader).
  const slimHeader = (
    <div className="sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-background/85 px-4 py-2.5 backdrop-blur-sm sm:px-6">
      <button
        type="button"
        onClick={toggleSidebar}
        title={leftCollapsed ? "사이드바 펼치기" : "사이드바 접기"}
        aria-label={leftCollapsed ? "사이드바 펼치기" : "사이드바 접기"}
        aria-expanded={!leftCollapsed}
        className={cn(
          "inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-border px-2.5 text-[13px] font-bold transition-colors focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none",
          leftCollapsed
            ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
            : "bg-card text-muted-foreground hover:text-foreground",
        )}
      >
        <PanelLeft aria-hidden="true" className="h-[17px] w-[17px]" />
        {leftCollapsed && <span>메뉴</span>}
      </button>

      <div className="ml-auto flex shrink-0 items-center">
        <Search />
      </div>
    </div>
  );

  // 우측 사이드바 풀이 패널 — ArticleShell 의 우측 aside 와 외관·동작 100% 일치.
  // chrome 은 SolutionAside 공유 컴포넌트. 레일(숨기기) 상태일 땐 폭 고정이라 리사이즈 핸들 숨김
  // (showRightResize={!solutionCollapsed} 으로 ShellFrame 에 위임).
  const solutionAside = (
    <SolutionAside
      activeSolutionId={activeSolutionId}
      solutionCollapsed={solutionCollapsed}
      onHide={() => setSolutionCollapsed(true)}
      onExpand={expandSolution}
      onExit={exitSolution}
      wrongOpen={wrongOpen}
      setWrongOpen={setWrongOpen}
      splitRatio={splitRatio}
      solutionSplitRef={solutionSplitRef}
      onResizeSolutionSplit={onResizeSolutionSplit}
      setSolutionSlot={setSolutionSlot}
      setErrorNoteSlot={setErrorNoteSlot}
    />
  );

  const adAside = (
    <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden p-3">
      <AdSenseSidebar />
    </div>
  );

  return (
    <SlotRegistryContext.Provider value={slotRegistry}>
      <SolutionModeContext.Provider value={solutionMode}>
        <ShellFrame
          // 광고 또는 풀이 모드면 3컬럼, 광고 미노출·평상시엔 2컬럼.
          columns={hasRightColumn ? "left+main+right" : "left+main"}
          // 좌측 폭: 접힘 시 레일(58px), 그 외 leftW. resize 는 ForumProblemShell 소유(LS_LEFT_W).
          leftWidthCss={leftWidthCss}
          onResizeLeft={onResizeLeft}
          showLeftContent={!leftCollapsed}
          left={leftContent}
          // 우측 폭: 광고는 300px 고정, 풀이 모드는 solutionW / 숨기기 레일.
          rightWidthCss={rightWidthCss}
          onResizeRight={onResizeRight}
          showRightResize={isSolution && !solutionCollapsed}
          rightAsideClassName={
            isSolution ? "bg-gray-50 dark:bg-gray-900/40" : "bg-background"
          }
          rightAriaLabel={isSolution ? "풀이 및 오답노트" : "광고"}
          right={isSolution ? solutionAside : adAside}
          // 슬림 헤더(토글 + 검색) — ContentHeader 재사용 금지.
          renderHeader={slimHeader}
        >
          {/* children 이 이미 mx-auto max-w-4xl 래퍼를 포함하므로 max-width 를 재강제하지 않는다. */}
          <div className="px-4 pb-20 pt-2 sm:px-8">
            {children}
            {footer}
          </div>
        </ShellFrame>

        {globals}
      </SolutionModeContext.Provider>
    </SlotRegistryContext.Provider>
  );
}
